import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, FormGroup, Label, Form, Row, Col, Card, CardBody } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

const AddFarmer = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // 🔥 Machan, added 'type' here to match PHP validation requirements
    const [formData, setFormData] = useState({
        name: "",
        phone: "",
        type: "", // 'farmer' or 'trader'
        address: "",
        acc_no: "",
        ifsc_code: ""
    });

    // Error States
    const [errors, setErrors] = useState({});

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        if (errors[e.target.name]) {
            setErrors({ ...errors, [e.target.name]: "" });
        }
    };

    // 🔥 Frontend Validation Logic Updated to perfectly match API constraints
    const validateForm = () => {
        let formErrors = {};
        let isValid = true;

        // Name Validation
        if (!formData.name.trim()) {
            formErrors.name = "Name is required";
            isValid = false;
        }

        // Phone Validation (Strictly 10 digits as per API regex)
        if (!formData.phone.trim()) {
            formErrors.phone = "Phone number is required";
            isValid = false;
        } else if (!/^[0-9]{10}$/.test(formData.phone.trim())) {
            formErrors.phone = "Invalid phone number (10 digits required)";
            isValid = false;
        }

        // Type Validation (Required field in PHP API)
        if (!formData.type) {
            formErrors.type = "Please select Type (Farmer or Trader)";
            isValid = false;
        }

        // IFSC Validation (Matches PHP Regex: 4 alpha, 0, 6 alphanumeric)
        if (formData.ifsc_code.trim()) {
            const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
            if (!ifscRegex.test(formData.ifsc_code.trim().toUpperCase())) {
                formErrors.ifsc_code = "Invalid IFSC format (e.g., SBIN0001234)";
                isValid = false;
            }
        }

        // Account number validation (Minimum 6 characters as per PHP)
        if (formData.acc_no.trim() && formData.acc_no.trim().length < 6) {
            formErrors.acc_no = "Invalid account number (min 6 digits)";
            isValid = false;
        }

        setErrors(formErrors);

        if (!isValid) {
            toast.error("Please fill required fields correctly!", {
                toastId: "val-err"
            });
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        dispatch(loadingTrue());
        setErrors({});

        // Auto-format data for API safely
        const payload = {
            ...formData,
            ifsc_code: formData.ifsc_code.trim().toUpperCase(),
            phone: formData.phone.trim(),
            acc_no: formData.acc_no.trim()
        };

        try {
            const res = await Handler({
                method: "POST",
                url: "/farmer/create.php",
                data: payload
            });

            if (res?.success) {
                const displayType = formData.type === 'farmer' ? 'Farmer' : 'Trader';
                Swal.fire("Success!", `${displayType} registered successfully`, "success");
                navigate("/farmers");
            } else {
                if (res?.errors) {
                    setErrors(res.errors);
                } else {
                    toast.error(res?.message || "Something went wrong");
                }
            }
        } catch (err) {
            Swal.fire("Error", "Server error occurred", "error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    return (
        <div className="page-container p-1">
            <div className="page-header mb-4">
                <h6 className="text-muted">Management &gt; Registrations</h6>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h3 className="fw-bold">Add New Farmer / Trader</h3>
                        <p className="text-muted">Store and manage profiles seamlessly.</p>
                    </div>
                    <Button color="secondary" outline onClick={() => navigate(-1)}>
                        <i className="bx bx-arrow-back"></i> Back
                    </Button>
                </div>
            </div>

            <Card className="border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                <CardBody className="p-4">
                    <Form onSubmit={handleSubmit}>
                        {/* Basic Details */}
                        <Row>
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="name">Full Name *</Label>
                                    <Input
                                        type="text"
                                        name="name"
                                        id="name"
                                        placeholder="Enter name"
                                        className={errors.name ? "is-invalid" : ""}
                                        value={formData.name}
                                        onChange={handleChange}
                                    />
                                    {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="phone">Phone Number *</Label>
                                    <Input
                                        type="text"
                                        name="phone"
                                        id="phone"
                                        placeholder="10 digit number"
                                        maxLength="10"
                                        className={errors.phone ? "is-invalid" : ""}
                                        value={formData.phone}
                                        onChange={handleChange}
                                    />
                                    {errors.phone && <div className="invalid-feedback">{errors.phone}</div>}
                                </FormGroup>
                            </Col>
                            {/* 🔥 Added Type Radio Dropdown/Select for Farmer/Trader mapping */}
                            <Col md={4}>
                                <FormGroup>
                                    <Label for="type">Registration Type *</Label>
                                    <Input
                                        type="select"
                                        name="type"
                                        id="type"
                                        className={errors.type ? "is-invalid" : ""}
                                        value={formData.type}
                                        onChange={handleChange}
                                    >
                                        <option value="">Select Type</option>
                                        <option value="farmer">Farmer</option>
                                        <option value="trader">Trader</option>
                                    </Input>
                                    {errors.type && <div className="invalid-feedback">{errors.type}</div>}
                                </FormGroup>
                            </Col>
                        </Row>

                        {/* Bank Details */}
                        <Row className="mt-3">
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="acc_no">Account Number</Label>
                                    <Input
                                        type="text"
                                        name="acc_no"
                                        id="acc_no"
                                        placeholder="Enter account number"
                                        className={errors.acc_no ? "is-invalid" : ""}
                                        value={formData.acc_no}
                                        onChange={handleChange}
                                    />
                                    {errors.acc_no && <div className="invalid-feedback">{errors.acc_no}</div>}
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup>
                                    <Label for="ifsc_code">IFSC Code</Label>
                                    <Input
                                        type="text"
                                        name="ifsc_code"
                                        id="ifsc_code"
                                        placeholder="e.g., SBIN0001234"
                                        maxLength="11"
                                        className={errors.ifsc_code ? "is-invalid" : ""}
                                        value={formData.ifsc_code}
                                        onChange={handleChange}
                                    />
                                    {errors.ifsc_code && <div className="invalid-feedback">{errors.ifsc_code}</div>}
                                </FormGroup>
                            </Col>
                        </Row>

                        <Row className="mt-3">
                            <Col md={12}>
                                <FormGroup>
                                    <Label for="address">Address (Optional)</Label>
                                    <Input
                                        type="textarea"
                                        name="address"
                                        id="address"
                                        placeholder="Enter location/address details"
                                        rows="3"
                                        value={formData.address}
                                        onChange={handleChange}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>

                        <div className="mt-4 pt-3 border-top d-flex justify-content-end">
                            <Button type="reset" color="light" className="me-2" onClick={() => {
                                setFormData({ name: "", phone: "", type: "", address: "", acc_no: "", ifsc_code: "" });
                                setErrors({});
                            }}>
                                Clear
                            </Button>
                            <Button type="submit" className="add-btn px-5" color="primary">
                                <i className="bx bx-check-circle me-1"></i> Submit
                            </Button>
                        </div>
                    </Form>
                </CardBody>
            </Card>
        </div>
    );
};

export default AddFarmer;